import { Fragment, memo, useState } from 'react'
import { clsx } from 'clsx'

import css from './Navbar.module.scss'

import DebugInfoTooltip from '@/components/DebugInfoTooltip'

import GmiLogo from '@/assets/svgs/gmi-logo.svg?react'
import InfoIcon from '@/assets/svgs/info.svg?react'

import type { AppRoute } from '@/constants/appRoutes'

export type HealthStatus = {
  text: string
  color: 'yellow' | 'red' | 'green'
  status: 'loading' | 'error' | 'healthy'
}

export type ViewProps = {
  className?: string
  routeGroups: AppRoute[][]
  healthStatus: HealthStatus
  environment: string
  activeRoute: string
  onRouteNavigate: (to: string) => void
}

const View = memo(function NavbarView({
  className,
  routeGroups,
  healthStatus,
  environment,
  activeRoute,
  onRouteNavigate
}: ViewProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [isDebugInfoOpen, setIsDebugInfoOpen] = useState(false)
  const isDebugInfoVisible = hoveredItem === 'info' || isDebugInfoOpen

  return (
    <nav className={clsx('Navbar', css.root, className)}>
      <div className={clsx(css.section, css.sectionTop)}>
        <a
          href="/"
          className={clsx(css.iconLink, activeRoute === '/' && css.active)}
          onClick={(event) => {
            event.preventDefault()
            onRouteNavigate('/')
          }}
          onMouseEnter={() => setHoveredItem('logo')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <div className={clsx(css.icon, css.logo)}>
            <GmiLogo />
            <div className={clsx(css.tooltip, hoveredItem === 'logo' && css.visible)}>General Mills</div>
          </div>
        </a>
      </div>

      <div className={clsx(css.section, css.sectionCenter)}>
        {routeGroups.map((group, groupIndex) => (
          <Fragment key={groupIndex}>
            {groupIndex > 0 && <div className={css.routeDivider} role="separator" />}
            {group.map((route) => {
              const IconComponent = route.icon
              const isActive =
                activeRoute === route.to || (route.matchPrefix === true && activeRoute.startsWith(`${route.to}/`))
              return (
                <a
                  key={route.to}
                  href={route.to}
                  aria-current={isActive ? 'page' : undefined}
                  className={clsx(css.iconLink, isActive && css.active)}
                  onClick={(event) => {
                    event.preventDefault()
                    onRouteNavigate(route.to)
                  }}
                  onMouseEnter={() => setHoveredItem(route.to)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <div className={css.icon}>
                    <IconComponent />
                    <div className={clsx(css.tooltip, hoveredItem === route.to && css.visible)}>{route.label}</div>
                  </div>
                </a>
              )
            })}
          </Fragment>
        ))}
      </div>

      <div
        className={clsx(css.section, css.sectionBottom)}
        onMouseEnter={() => setHoveredItem('info')}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <button
          type="button"
          className={clsx(
            css.icon,
            css.infoTrigger,
            css[`icon${healthStatus.status.charAt(0).toUpperCase()}${healthStatus.status.slice(1)}`]
          )}
          aria-label="Toggle API debug panel"
          aria-expanded={isDebugInfoVisible}
          onClick={() => setIsDebugInfoOpen((open) => !open)}
        >
          <InfoIcon aria-hidden="true" />
        </button>
        <div className={clsx(css.infoTooltip, isDebugInfoVisible && css.infoTooltipVisible)}>
          <DebugInfoTooltip
            environment={environment}
            apiStatus={{ text: healthStatus.text, color: healthStatus.color }}
            visible={isDebugInfoVisible}
          />
        </div>
      </div>
    </nav>
  )
})

View.displayName = 'Navbar_View'

export default View
